---
title: "An integration stopped working"
description: "Gmail, Google Calendar, or Notion shows disconnected in Chosen, or features that depend on one went quiet. The usual causes and how to reconnect each one."
category: "troubleshooting"
keywords:
  - "integration disconnected"
  - "Gmail integration not working"
  - "calendar integration broken"
  - "Notion not syncing"
  - "reconnect integration"
schema: "HowTo"
nav:
  section: "troubleshooting"
  order: 40
  parent: "/troubleshooting"
---

An integration shows disconnected, or a feature that depends on one — outreach, scheduling, Notion sync — went quiet. Integrations break in a few predictable ways, and reconnecting takes about ten seconds. The catch is that integrations fail *quietly*, so the first step is always to go look. Everything below starts at **Settings → Integrations**.

## An OAuth token expired or access was revoked

Gmail and Google Calendar connect through Google's OAuth. Those access tokens expire on their own, and anyone can revoke Chosen's access from the Google account side — at which point Chosen is disconnected and won't tell you mid-task.

**Check it:** open **Settings → Integrations**. A greyed-out or disconnected Gmail or Calendar is the usual cause when [outreach](/guides/sourcing/outreach-sending) stops sending or the [scheduling assistant](/guides/scheduling/email-assistant) goes quiet.

**Fix it:**

<Steps>
  <li>Go to **Settings → Integrations**.</li>
  <li>Click **Connect** next to the integration that's disconnected.</li>
  <li>Sign in with Google and grant access again. Use the same address you recruit from.</li>
</Steps>

If you previously narrowed Chosen's Google permissions and the scheduling assistant got less useful, that's the trade-off — reconnecting with the broader scope restores it. See [connecting Gmail](/getting-started/integrations/gmail) for what each permission covers.

## Notion's database selection was cleared

Notion behaves differently. If you disconnect and reconnect Notion — or connect a different workspace — Chosen forgets which database it was syncing into. The integration shows connected, but **nothing syncs** until you pick a database again, and it fails silently.

**Check it:** if [Notion](/getting-started/integrations/notion) stopped syncing right after a reconnect or a workspace change, the database selection is almost certainly the cause.

**Fix it:**

<Steps>
  <li>Go to **Settings → Integrations** and open the Notion integration.</li>
  <li>Re-select the database you want to sync into.</li>
  <li>Review the field mapping while you're there — which Chosen fields map to which Notion properties — in case it needs adjusting.</li>
</Steps>

## A teammate connected theirs, not yours

Gmail and Calendar integrations are **per-person**, not per-company. There's no single org-wide inbox or calendar. A teammate connecting their Gmail does nothing for your account — your outreach sends from your address, and the scheduler can only see calendars their owners have connected.

**Check it:** if scheduling books over your focus time, or your outreach won't send while a colleague's works fine, check whether *you* have connected the integration — under your own **Settings → Integrations**.

**Fix it:** connect your own Gmail and your own Google Calendar. On a shared interview loop, every interviewer needs to connect their own calendar — otherwise the [scheduler](/guides/scheduling/ai-scheduler-overview) is solving the puzzle with missing pieces.

<Callout type="warning" title="Integrations fail without an alert">
A disconnected integration doesn't pop a warning — outreach just stops, sync just stalls. If a feature that depends on Gmail, Calendar, or Notion goes quiet, check **Settings → Integrations** before assuming the feature itself is broken. A greyed-out integration is the answer surprisingly often.
</Callout>

## Still stuck?

If an integration shows connected, you've re-selected the Notion database where relevant, and the dependent feature still doesn't work, open **Settings → Contact support**. Mention which integration, when it last worked, and what changed in between.
